"use client";

import { Suspense, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import Script from "next/script";

import {
  CONSENT_EVENT,
  META_PIXEL_ID,
  isPixelConfigured,
  isTrackingAllowed,
  trackPageView,
} from "@/lib/metaPixel";

/**
 * Meta Pixel loader and SPA PageView tracker.
 *
 * Replaces the previous inline snippet in layout.tsx, which had two bugs that
 * cost a large share of the data:
 *
 *  1. It only loaded on the first scroll or click, so a visitor who landed,
 *     read, and left was never counted — and never got an _fbp cookie, so no
 *     later conversion could be attributed to their ad click.
 *  2. It fired PageView exactly once. This is an App Router SPA, so every
 *     client-side navigation after the entry page was invisible.
 */

function PageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // The pixel's own init already fires the first PageView; without this guard
  // the entry page would be counted twice.
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    trackPageView();
  }, [pathname, searchParams]);

  return null;
}

export default function MetaPixel() {
  useEffect(() => {
    // Someone pressing "Reject all" mid-session stops tracking immediately,
    // rather than only from the next page load.
    const onConsentChange = () => {
      if (!isTrackingAllowed() && typeof window.fbq === "function") {
        window.fbq("consent", "revoke");
      }
    };

    window.addEventListener(CONSENT_EVENT, onConsentChange);
    return () => window.removeEventListener(CONSENT_EVENT, onConsentChange);
  }, []);

  // No pixel configured for this environment (e.g. local dev or a preview
  // deploy) — render nothing at all rather than a script that would init with
  // an empty id and log errors on every page.
  if (!isPixelConfigured) return null;

  return (
    <>
      <Script id="meta-pixel" strategy="afterInteractive">
        {`
          !function(f,b,e,v,n,t,s){
            if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window,document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');

          try {
            if (sessionStorage.getItem('cookie_consent') === 'rejected') {
              fbq('consent', 'revoke');
            }
          } catch (e) {}

          fbq('init', '${META_PIXEL_ID}');
          fbq('track', 'PageView');
        `}
      </Script>

      {/*
        useSearchParams needs a Suspense boundary or it opts the whole route
        out of static rendering.
      */}
      <Suspense fallback={null}>
        <PageViewTracker />
      </Suspense>

      <noscript>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          alt=""
          src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
        />
      </noscript>
    </>
  );
}
